import { useCallback, useEffect, useState } from 'react';
import { IdentifiantCopiable } from '../components/IdentifiantCopiable';
import { useNavigate } from 'react-router-dom';
import {
  ACCES_ESPACE,
  accesEspace,
  LIBELLES_ROLE_ESPACE,
  type AccesEspace,
  type SpaceInvitationView,
  type SpaceMemberView,
  type SpaceOverview,
  type SpaceRole,
} from '@authsemply/shared';
import { Alert } from '../components/Alert';
import { AuthCard } from '../components/AuthCard';
import { ApiError } from '../lib/api';
import { apiEspace, nomMembre } from '../lib/espace';
import { useAuthStore } from '../store/auth.store';
import { CompteBetaAvis } from '../components/CompteBetaAvis';

/**
 * Mon espace : qui y a accès, avec quel rôle, et à qui il appartient.
 *
 * C'est le seul endroit de la suite où la propriété se transfère. Les routes
 * existaient déjà ; sans cet écran, le refus « transférez d'abord la propriété »
 * renvoyé au retrait du propriétaire n'indiquait aucun endroit où le faire.
 *
 * Le rôle affiché vient du serveur à chaque lecture, jamais d'un état local
 * mis à jour après coup : un transfert rétrograde l'appelant au même instant,
 * et l'écran doit le montrer plutôt que de le déduire.
 */

/**
 * Accès attribuables (04/10/2026) : le rôle ET le profil en un seul choix.
 * « Lecture seule » répond au besoin de l'expert-comptable invité — avant, le
 * seul choix « Membre » valait accès standard partout.
 */
const ACCES_ATTRIBUABLES: AccesEspace[] = ['MEMBER_LECTURE_SEULE', 'MEMBER', 'ADMIN'];

/** Libellé court d'un accès, pour la ligne d'un membre ou d'une invitation. */
function libelleAcces(role: SpaceRole, profil?: 'STANDARD' | 'LECTURE_SEULE'): string {
  const acces = accesEspace(role, profil);
  if (acces === 'OWNER') return LIBELLES_ROLE_ESPACE.OWNER;
  if (acces === 'MEMBER_LECTURE_SEULE') return 'Membre (lecture seule)';
  return LIBELLES_ROLE_ESPACE[role];
}

export function EspacePage() {
  const navigate = useNavigate();
  const { user, loading: chargementCompte, fetchMe } = useAuthStore();

  const [apercu, setApercu] = useState<SpaceOverview | null>(null);
  const [membres, setMembres] = useState<SpaceMemberView[] | null>(null);
  const [invitations, setInvitations] = useState<SpaceInvitationView[]>([]);
  const [erreur, setErreur] = useState<string | null>(null);
  const [succes, setSucces] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  /** Membre pour lequel le transfert de propriété attend une confirmation. */
  const [transfertVise, setTransfertVise] = useState<string | null>(null);
  // Retrait d'un membre : confirmé en deux temps, comme le transfert — un
  // clic de trop ne doit pas couper l'accès de quelqu'un (06/10/2026).
  const [retraitVise, setRetraitVise] = useState<string | null>(null);

  const [emailInvite, setEmailInvite] = useState('');
  // Par défaut l'accès le PLUS restreint (04/10/2026) : élargir est un geste
  // volontaire, oublier de restreindre ne doit pas ouvrir l'accès complet.
  const [accesInvite, setAccesInvite] = useState<AccesEspace>('MEMBER_LECTURE_SEULE');

  const recharger = useCallback(async () => {
    const [a, m, i] = await Promise.all([
      apiEspace.apercu(),
      apiEspace.membres(),
      apiEspace.invitations().catch(() => [] as SpaceInvitationView[]),
    ]);
    setApercu(a);
    setMembres(m);
    setInvitations(i);
  }, []);

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  useEffect(() => {
    if (chargementCompte) return;
    if (!user) {
      navigate('/login');
      return;
    }
    if (user.mfaRequired) {
      navigate('/double-authentification');
      return;
    }
    void recharger().catch((e: unknown) => setErreur(message(e)));
  }, [chargementCompte, user, navigate, recharger]);

  /**
   * Enveloppe commune : une action à la fois, l'erreur du serveur affichée
   * telle quelle, et une relecture systématique. Les invariants d'espace sont
   * tenus côté serveur — l'écran ne les rejoue pas, il en montre le résultat.
   */
  async function agir(action: () => Promise<unknown>, messageSucces: string) {
    setEnCours(true);
    setErreur(null);
    setSucces(null);
    try {
      await action();
      await recharger();
      await fetchMe();
      setSucces(messageSucces);
    } catch (e) {
      setErreur(message(e));
    } finally {
      setEnCours(false);
      setTransfertVise(null);
    }
  }

  if (chargementCompte || !membres || !apercu) {
    return <AuthCard title="Chargement…">{null}</AuthCard>;
  }

  const monRole = apercu.role;
  const jeSuisProprietaire = monRole === 'OWNER';
  const jePeuxInviter = jeSuisProprietaire || monRole === 'ADMIN';
  // Compte bêta partagé (03/10/2026) : membres et invitations grisés — l'API
  // les refuse de toute façon (`beta_compte_verrouille`).
  const verrou = Boolean(user?.betaCompte);

  return (
    <AuthCard
      title="Mon espace"
      subtitle={apercu.space?.name ?? 'Espace SEmply'}
    >
      <p style={{ fontSize: '0.85rem', color: 'var(--text-color-light)', margin: '0 0 1.25rem' }}>
        Vous êtes <strong>{LIBELLES_ROLE_ESPACE[monRole].toLowerCase()}</strong> de cet espace.
        Le propriétaire et les administrateurs ont les mêmes droits dans les produits ; seul le
        propriétaire porte l’abonnement et peut le céder.
      </p>

      {apercu.space ? <IdentifiantCopiable libelle="Identifiant de l’espace" valeur={apercu.space.id} /> : null}

      {verrou ? <CompteBetaAvis /> : null}
      <Alert message={erreur} />
      <Alert message={succes} variant="success" />

      <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>
        Personnes ayant accès ({membres.length})
      </h2>
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem' }}>
        {membres.map((membre) => {
          const cestMoi = membre.user.id === user?.id;
          const estProprietaire = membre.role === 'OWNER';
          return (
            <li
              key={membre.id}
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '0.75rem',
                padding: '0.7rem 0',
                borderBottom: '1px solid var(--color-border)',
              }}
            >
              <span style={{ minWidth: '12rem' }}>
                <strong>{nomMembre(membre)}</strong>
                {cestMoi ? <span style={{ fontWeight: 400 }}> — vous</span> : null}
                <div style={{ color: 'var(--text-color-light)', fontSize: '0.8rem' }}>
                  {membre.user.email} — {libelleAcces(membre.role, membre.profil)} depuis le{' '}
                  {new Date(membre.createdAt).toLocaleDateString('fr-FR')}
                </div>
              </span>

              <span
                style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', alignItems: 'center', maxWidth: '100%' }}
              >
                {/* Le rôle du propriétaire ne se change pas depuis sa propre
                    ligne : on désigne quelqu'un d'autre, ce qui le rétrograde. */}
                {jeSuisProprietaire && !estProprietaire ? (
                  <select
                    aria-label={`Accès de ${nomMembre(membre)}`}
                    value={accesEspace(membre.role, membre.profil)}
                    disabled={enCours || verrou}
                    onChange={(e) => {
                      const cible = ACCES_ESPACE[e.target.value as AccesEspace];
                      void agir(async () => {
                        // Le rôle d'abord (une promotion remet le profil à
                        // STANDARD côté API), puis le profil d'un membre.
                        if (cible.role !== membre.role) {
                          await apiEspace.changerRole(membre.user.id, cible.role);
                        }
                        if (cible.role === 'MEMBER') {
                          await apiEspace.changerProfil(membre.user.id, cible.profil);
                        }
                      }, `Accès de ${nomMembre(membre)} mis à jour.`);
                    }}
                    style={{ padding: '0.35rem 0.5rem', fontSize: '0.8rem', maxWidth: '100%' }}
                  >
                    {ACCES_ATTRIBUABLES.map((acces) => (
                      <option key={acces} value={acces}>
                        {ACCES_ESPACE[acces].libelle}
                      </option>
                    ))}
                  </select>
                ) : null}

                {jeSuisProprietaire && !estProprietaire ? (
                  transfertVise === membre.user.id ? (
                    <>
                      <button
                        className="btn"
                        type="button"
                        disabled={enCours || verrou}
                        style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                        onClick={() =>
                          void agir(
                            () => apiEspace.changerRole(membre.user.id, 'OWNER'),
                            `${nomMembre(membre)} est désormais propriétaire de l’espace. ` +
                              'Vous en êtes administrateur.',
                          )
                        }
                      >
                        Confirmer
                      </button>
                      <button
                        className="btn btn-secondary"
                        type="button"
                        disabled={enCours || verrou}
                        style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                        onClick={() => setTransfertVise(null)}
                      >
                        Annuler
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      disabled={enCours || verrou}
                      style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                      onClick={() => {
                        setSucces(null);
                        setErreur(null);
                        setTransfertVise(membre.user.id);
                      }}
                    >
                      Rendre propriétaire
                    </button>
                  )
                ) : null}

                {jePeuxInviter && !estProprietaire && !cestMoi ? (
                  retraitVise === membre.user.id ? (
                    <>
                      <button
                        className="btn"
                        type="button"
                        disabled={enCours || verrou}
                        style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                        onClick={() => {
                          setRetraitVise(null);
                          void agir(
                            () => apiEspace.retirerMembre(membre.user.id),
                            `${nomMembre(membre)} n’a plus accès à l’espace.`,
                          );
                        }}
                      >
                        Confirmer le retrait
                      </button>
                      <button
                        className="btn btn-secondary"
                        type="button"
                        disabled={enCours || verrou}
                        style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                        onClick={() => setRetraitVise(null)}
                      >
                        Annuler
                      </button>
                    </>
                  ) : (
                    <button
                      className="btn btn-secondary"
                      type="button"
                      disabled={enCours || verrou}
                      style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                      onClick={() => {
                        setSucces(null);
                        setErreur(null);
                        setTransfertVise(null);
                        setRetraitVise(membre.user.id);
                      }}
                    >
                      Retirer
                    </button>
                  )
                ) : null}
              </span>
            </li>
          );
        })}
      </ul>

      {/* Le message qui accompagne le bouton de transfert : il dit ce qui va se
          passer AVANT de le faire, parce que c'est irréversible sans l'accord
          du nouveau propriétaire. */}
      {transfertVise ? (
        <p
          style={{
            fontSize: '0.85rem',
            margin: '-0.75rem 0 1.25rem',
            color: 'var(--text-color-light)',
          }}
        >
          En confirmant, vous cédez la propriété de l’espace — abonnements et facturation
          compris — et devenez administrateur. Seul le nouveau propriétaire pourra vous la
          rendre.
        </p>
      ) : null}

      {invitations.length > 0 ? (
        <>
          <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Invitations en attente</h2>
          <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 1.5rem', fontSize: '0.9rem' }}>
            {invitations.map((invitation) => (
              <li
                key={invitation.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: '1rem',
                  padding: '0.6rem 0',
                  borderBottom: '1px solid var(--color-border)',
                }}
              >
                <span>
                  <strong>{invitation.email}</strong>
                  <div style={{ color: 'var(--text-color-light)', fontSize: '0.8rem' }}>
                    {libelleAcces(invitation.role, invitation.profil)} — expire le{' '}
                    {new Date(invitation.expiresAt).toLocaleDateString('fr-FR')}
                  </div>
                </span>
                {jePeuxInviter ? (
                  <button
                    className="btn btn-secondary"
                    type="button"
                    disabled={enCours || verrou}
                    style={{ width: 'auto', padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                    onClick={() =>
                      void agir(
                        () => apiEspace.annulerInvitation(invitation.id),
                        'Invitation annulée.',
                      )
                    }
                  >
                    Annuler
                  </button>
                ) : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      {jePeuxInviter ? (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const adresse = emailInvite.trim();
            if (!adresse) return;
            const choix = ACCES_ESPACE[accesInvite];
            void agir(
              () => apiEspace.inviter(adresse, choix.role, choix.profil),
              `Invitation envoyée à ${adresse}.`,
            ).then(
              () => setEmailInvite(''),
            );
          }}
        >
          <h2 style={{ fontSize: '1rem', margin: '0 0 0.5rem' }}>Inviter quelqu’un</h2>
          <div className="field">
            <label htmlFor="email-invite">Adresse e-mail</label>
            <input
              id="email-invite"
              name="email-invite"
              type="email"
              autoComplete="off"
              value={emailInvite}
              onChange={(e) => setEmailInvite(e.target.value)}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="role-invite">Accès</label>
            <select
              id="role-invite"
              name="role-invite"
              value={accesInvite}
              onChange={(e) => setAccesInvite(e.target.value as AccesEspace)}
              style={{ padding: '0.6rem 0.7rem', fontSize: '1rem' }}
            >
              {ACCES_ATTRIBUABLES.map((acces) => (
                <option key={acces} value={acces}>
                  {ACCES_ESPACE[acces].libelle}
                </option>
              ))}
            </select>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-color-light)', margin: '0.35rem 0 0' }}>
              La personne invitée garde son propre compte SEmply si elle en a déjà un.
              En lecture seule, elle consulte tous les dossiers de l’espace sans rien
              pouvoir modifier.
            </p>
          </div>
          <button className="btn" type="submit" disabled={enCours || verrou}>
            Envoyer l’invitation
          </button>
        </form>
      ) : null}

      <div className="links">
        <a href="/compte">Mon compte</a>
        <a href="/produits">Mes outils</a>
      </div>
    </AuthCard>
  );
}

/** Message du serveur quand il y en a un — jamais remplacé par un texte générique. */
function message(e: unknown): string {
  if (e instanceof ApiError) return e.message;
  return 'Une erreur est survenue.';
}
